import React, { useContext, useState, useEffect } from "react";
import { Card, TextField, Checkbox, FormLayout, Layout, PageActions, TextContainer } from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/react-hooks";
import PropTypes from "prop-types";
import { Editor } from "@tinymce/tinymce-react";

import config from "configuration";

// import component
import { SkeletonAnnotated } from "lib/components";
import constant from "lib/constant/constant";

import { PrivateContext, OnboardingContext } from "lib/context";

import { baseHelper, errorHelper } from "lib/helpers";

import { SEND_BULK_INVITE_BY_CSV } from "app/vendors/apollo/mutations";
import { GET_EMAIL_TEMPLATE } from "app/vendors/apollo/queries";
import FileUpload from "lib/components/fileInput/fileUpload";

const BulkInvite = (props) => {
  const { history, cms } = useContext(PrivateContext);
  const { isOnboarding = false, onNext, onPrevious } = useContext(OnboardingContext);

  const {
    // learnMore
    setBanner,
  } = props;
  const { data, loading } = useQuery(GET_EMAIL_TEMPLATE);
  const [bulkInviteRequest, { loading: bulkInviteLoading }] = useMutation(SEND_BULK_INVITE_BY_CSV);
  const [messageData, setMessageData] = useState(null);
  const [subject, setSubject] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [values, setValues] = useState();

  const [fileName, setFileName] = useState("");

  useEffect(() => {
    if (messageData || subject || fileData) {
      setBanner({
        isOpen: false,
        status: "",
        title: "",
      });
    }
  }, [messageData, subject, fileData, setBanner]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const resError = baseHelper.getResponseError(data, constant.gql.GET_BULK_EMAIL_TEMPLATE);
    if (resError) {
      setBanner({ isOpen: true, title: resError, status: constant.CRITICAL });
      return;
    }
    const resData = baseHelper.getResponseData(data, constant.gql.GET_BULK_EMAIL_TEMPLATE);
    setSubmitButtonLoading(false);
    setMessageData(resData.message);
    setSubject(resData.subject);
  }, [data, setBanner]);

  useEffect(() => {
    if (values && values.data) {
      setFileName(values.fileName);
      const csvValue = values.data.trim();
      const regexForEnterKey = /\r\n/g;
      const regexForComma = /,/g;
      let csvDataAfterRegex = csvValue.replace(regexForEnterKey, "\r\n");
      csvDataAfterRegex = csvDataAfterRegex.replace(regexForComma, "\r");
      setFileData(csvDataAfterRegex);
    }
  }, [values]);

  const onSubmit = async () => {
    if (!fileData) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.csvIsRequired"),
      });
      return;
    }
    if (!subject) {
      return;
    }

    const requestData = {
      file: {
        file: fileData,
        fileName,
        isReadOnly,
      },
      message: { message: messageData, subject },
    };
    try {
      const res = await bulkInviteRequest({
        variables: {
          input: requestData,
        },
      });
      const resData = baseHelper.getResponseData(res.data, constant.gql.SEND_BULK_INVITE_BY_CSV);
      let banner = {
        action: {
          id: "viewLogs",
          content: cms("section.bulkInvite.message.success.action"),
          onAction: () => history.push("/activity/bulk"),
        },
        isOpen: true,
        status: constant.SUCCESS,
        title: cms("section.bulkInvite.message.success.title"),
        children: cms("section.bulkInvite.message.success.content"),
      };

      if (!resData) {
        const error = baseHelper.getResponseError(res.data, constant.gql.SEND_BULK_INVITE_BY_CSV);
        banner = {
          action: null,
          children: null,
          isOpen: true,
          status: constant.CRITICAL,
          title: error,
        };
      }
      setIsSubmitDisabled(true);
      setBanner({
        action: banner.action,
        children: banner.children,
        isOpen: banner.isOpen,
        status: banner.status,
        title: banner.title,
      });
      if (isOnboarding && resData) {
        onNext();
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const isAnyEmptyField = () => {
    return !(fileData && subject && messageData);
  };

  const description = [cms("section.bulkInvite.description.para1"), cms("section.bulkInvite.description.para2")];
  return (
    <>
      {loading || submitButtonLoading ? (
        <SkeletonAnnotated />
      ) : (
        <div>
          <Layout.AnnotatedSection
            title={cms("section.bulkInvite.title")}
            description={description.map((text) => (
              <React.Fragment key={text}>
                <p>{text}</p>
                <br />
              </React.Fragment>
            ))}
          >
            <Card
              title={cms("section.bulkInvite.title")}
              // actions={[
              //   {
              //     content: cms("section.bulkInvite.learnMore"),
              //     onAction: () => learnMore(cms("section.bulkInvite.title"), cms("section.bulkInvite.todo")),
              //   },
              // ]}
            >
              <Card.Section>
                <FormLayout>
                  <TextContainer>{cms("section.bulkInvite.description.para3")}</TextContainer>
                  <FileUpload
                    setFileData={setFileData}
                    setValue={setValues}
                    downloadCSV={`${config.rootURL}/file-download/invite`}
                    values={values}
                    setIsSubmitDisabled={setIsSubmitDisabled}
                  />
                  <TextField
                    id="textField"
                    label={`${cms("section.invite.label.subject")}*`}
                    value={subject}
                    onChange={(value) => setSubject(value)}
                    error={!subject && cms("message.subjectIsRequired")}
                  />
                  <div id="csvSection">
                    <label htmlFor="idTiny">
                      <b>{`${cms("section.bulkInvite.label.message")}*`}</b>
                    </label>
                    <Editor
                      id="idTiny"
                      textareaName={constant.providerList.DESCRIPTION}
                      value={messageData}
                      onEditorChange={(value) => setMessageData(value)}
                      init={{
                        menubar: true,
                        plugins: [cms("section.bulkInvite.plugins")],
                        toolbar: cms("section.bulkInvite.toolbar"),
                      }}
                    />
                  </div>
                </FormLayout>
              </Card.Section>
            </Card>
            <Card title={cms("section.bulkInvite.vendorAccessPermission.title")}>
              <Card.Section>
                <TextContainer>
                  <p>{cms("section.bulkInvite.vendorAccessPermission.description.para1")}</p>
                  <p>{cms("section.bulkInvite.vendorAccessPermission.description.para2")}</p>
                </TextContainer>
                <br />
                <FormLayout>
                  <Checkbox
                    id="readPermission"
                    label={cms("common.label.isReadOnlyVendor")}
                    checked={isReadOnly}
                    onChange={() => setIsReadOnly(!isReadOnly)}
                  />
                </FormLayout>
              </Card.Section>
            </Card>
            <PageActions
              primaryAction={{
                content: cms("common.button.submit"),
                id: "submitButton",
                onAction: () => onSubmit(),
                loading: bulkInviteLoading,
                disabled: isAnyEmptyField() || isSubmitDisabled,
              }}
              secondaryActions={[
                {
                  content: cms("common.button.cancel"),
                  id: "cancelButton",
                  onAction: () => (isOnboarding ? onPrevious() : history.push("/")),
                },
              ]}
            />
          </Layout.AnnotatedSection>
        </div>
      )}
    </>
  );
};
BulkInvite.propTypes = {
  setBanner: PropTypes.func.isRequired,
  // learnMore: PropTypes.func.isRequired,
};
export default BulkInvite;
